import React, {
  useEffect, useState, useCallback, useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import ContentWrap from 'core/modules/Partials/ContentWrap/ContentWrap';
import List from 'core/modules/Partials/List/List';
import MerchantTileListItem from 'core/modules/MerchantTileList/components/MerchantTileListItem/MerchantTileListItem';
import { selectSiteUrl } from 'core/selectors/app';
import { selectIsMobileMQ, selectActiveMQ } from 'core/modules/MediaQueries/selectors';
import { selectUserIsIdentified } from 'core/selectors/user';
import { useDefineInitialParams } from 'core/hooks/useDefineInitialParams';

import { makeSelectTopStoresConfig } from './selectors';
import { triggerTopStoresIsViewed } from './actions';
import { useGetMerchants } from './hooks/useGetMerchants';

import './TopStores.scss';

function TopStores({ isAllStores, label, isPersonalizedMhp }) {
  const dispatch = useDispatch();

  const selectTopStoresConfig = useMemo(makeSelectTopStoresConfig, []);
  const config = useSelector(state => selectTopStoresConfig(state, isPersonalizedMhp));
  const siteUrl = useSelector(selectSiteUrl);
  const isMobileMQ = useSelector(selectIsMobileMQ);
  const activeMQ = useSelector(selectActiveMQ);
  const isUserIdentified = useSelector(selectUserIsIdentified);

  const { tagHelper } = config;

  const handleRef = useCallback((ref) => {
    tagHelper(ref); // handle Chase tagging implementation
  }, []);

  const { isLoaded, merchants } = useGetMerchants(config);

  useEffect(() => {
    if (isLoaded && merchants.length) {
      dispatch(triggerTopStoresIsViewed(merchants));
    }
  }, [merchants, isLoaded, dispatch]);

  const {
    carousel, secTitle, secTitlePill, useAddToFavorite, shouldTrackInitialParams, api,
    getUseSmallerIcon, getShouldClientUseExpandableView, minMerchantsExpanadbleView, maxMerchantsExpanadbleView,
    viewAllLinkCTAText, getViewAllLinkText, getViewAllLinkUrl, checkShouldUseCarousel,
  } = config;
  const shouldUseCarousel = checkShouldUseCarousel(activeMQ);
  const viewAllLinkUrl = getViewAllLinkUrl(siteUrl);
  const { sectionId } = api.options;
  const trackingParams = useDefineInitialParams(shouldTrackInitialParams, { sectionId });

  const [isExpandButtonClicked, setIsExpandButtonClicked] = useState(false);

  const useSmallerIcon = getUseSmallerIcon(isMobileMQ);

  const handleTopStoresButton = () => {
    setIsExpandButtonClicked(true);
  };

  const renderButton = () => {
    const shouldShowExpandButton =
      !isExpandButtonClicked && merchants.length > minMerchantsExpanadbleView;

    const renderExpandButton = () => (
      <button
        onClick={handleTopStoresButton}
        className="mn_button mn_secondaryButton mn_seeMoreButton"
      >
        See more
      </button>
    );

    const renderViewAllLink = () => (
      <a className="mn_button mn_secondaryButton mn_seeAllButton" href={viewAllLinkUrl}>
        {viewAllLinkCTAText}
      </a>
    );

    return (
      <div className="mn_topStoresButtonContainer">
        { shouldShowExpandButton ? renderExpandButton() : renderViewAllLink() }
      </div>
    );
  };

  const isTopStoresExpandableView = getShouldClientUseExpandableView(isUserIdentified, isMobileMQ);
  const maxMerchants = isExpandButtonClicked ? maxMerchantsExpanadbleView : minMerchantsExpanadbleView;
  const merchantsToShow = isTopStoresExpandableView ? merchants.slice(0, maxMerchants) : merchants;

  if (isEmpty(merchants)) {
    return null;
  }

  const viewAllLinkText = getViewAllLinkText();

  return (
    <div
      className={`${isTopStoresExpandableView ? 'mn_topStoresExpandable' : 'mn_topStores'}`}
      ref={handleRef}
    >
      <ContentWrap mobile tablet l xl xxl={!isPersonalizedMhp}>
        <SectionTitle title={secTitle} pill={secTitlePill}>
          {isAllStores && <a className="mn_viewAllLink" href={`${siteUrl}/b____.htm`}>{viewAllLinkText}</a>}
        </SectionTitle>
        <div className="mn_sectionContent">
          <List options={carousel} shouldUseCarousel={shouldUseCarousel}>
            {merchantsToShow.map((merchant) => (
              <MerchantTileListItem
                key={merchant.id}
                merchant={merchant}
                useAddToFavorite={useAddToFavorite}
                useSmallerIcon={useSmallerIcon && !isTopStoresExpandableView}
                label={label}
                trackingParams={trackingParams}
              />
            ))}
          </List>
        </div>
        {isTopStoresExpandableView && renderButton()}
      </ContentWrap>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><TopStores {...props} /></ComponentErrorBoundary>;
